/**
 * The chart tooltip — **it names the thing, not just the numbers.**
 *
 * Recharts' default renders the axis values, which on a scatter of 300 episodes
 * answers *"what are the coordinates of this dot"* and not the only question a
 * reader has: **which episode is that?** Without the title, an outlier is
 * information you cannot act on — you can see that something reached 11m views
 * and held nobody, and you cannot go and look at it.
 *
 * Rendered with the same tokens as `Hint`, so a hover reads the same wherever
 * it happens.
 */
import type { ReactNode } from 'react'

const NUMBER = new Intl.NumberFormat('en-US')

const CARD = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-pop)',
  padding: '0.45rem 0.6rem',
  fontSize: '0.75rem',
  lineHeight: 1.45,
  maxWidth: '20rem',
}

const numberStyle = { fontVariantNumeric: 'tabular-nums' } as const

export interface TipRow {
  label: string
  value: string
}

export function TipCard({
  title,
  subtitle,
  rows,
}: {
  title: ReactNode
  subtitle?: ReactNode
  rows: TipRow[]
}) {
  return (
    <div style={CARD}>
      <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
        {title}
      </div>
      {subtitle && (
        <div className="mb-1 text-xs" style={{ color: 'var(--text-label)' }}>
          {subtitle}
        </div>
      )}
      {rows.map((row) => (
        <div key={row.label} className="flex justify-between gap-3">
          <span style={{ color: 'var(--text-label)' }}>{row.label}</span>
          <span style={{ ...numberStyle, color: 'var(--text-primary)' }}>{row.value}</span>
        </div>
      ))}
    </div>
  )
}

export function formatCount(value: number): string {
  return NUMBER.format(Math.round(value))
}
